import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth.service';
import { fileService } from '@/services/file.service';
import type { User, MfaRequiredResponse } from '@/types/user.types';

export interface LoginResult {
  requiresMfa: boolean;
  mfaToken?: string;
  emailHint?: string;
}

function isMfaRequired(result: any): result is MfaRequiredResponse {
  return result && result.requires_mfa === true;
}

export function useAuth() {
  const navigate = useNavigate();
  const store = useAuthStore();

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResult> => {
      const response = await authService.login({ username, password });

      if (isMfaRequired(response)) {
        return {
          requiresMfa: true,
          mfaToken: response.mfa_token,
          emailHint: response.email_hint,
        };
      }

      // Update Zustand store
      store.login(response.token, response.user);
      // QA #102: send the upload failures kept while the session was dead.
      void fileService.flushUploadFailures();

      // If must change password, redirect to password page
      if (response.user.must_change_password) {
        navigate('/password');
      } else {
        navigate('/overview');
      }

      return { requiresMfa: false };
    },
    [navigate, store]
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      store.logout();
      navigate('/login', { replace: true });
    }
  }, [navigate, store]);

  const refreshProfile = useCallback(async (): Promise<User | null> => {
    try {
      const user = await authService.getProfile();

      if (store.token) {
        store.login(store.token, user);
      }

      return user;
    } catch (error) {
      console.error('Failed to refresh profile:', error);
      return null;
    }
  }, [store]);

  return {
    // State
    isAuthenticated: store.isAuthenticated,
    user: store.user,
    token: store.token,

    // Actions
    login,
    logout,
    refreshProfile,
    // Role helpers live on useAuthStore (isAdmin / hasRole / canManage*);
    // the copies that used to be returned here had no reader (F-10, X-4).
  };
}
